import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, PlayCircle, Eye } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

// The October 2026 content audit. Matched on slug, so the same button applies
// the identical corrections in production — and says what it changed.
export default function AskCassieAudit() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => adminApi.askCassie.auditStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const run = async (dry) => {
    setBusy(dry ? "dry" : "apply");
    try {
      const r = await adminApi.askCassie.auditApply(dry);
      setReport(r);
      if (!dry) {
        await load();
        toast({ title: `${r.updated.length} answers corrected`, description: "The library and Cassie now use the corrected wording." });
      }
    } catch {
      toast({ title: "Couldn't run the corrections", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (!status) return <p className="text-sm text-slate-400">Loading…</p>;

  const pending = status.corrections_available - status.already_applied;

  return (
    <div data-testid="ask-cassie-audit">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-navy">Content audit — 2 October 2026</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
              The corrections the audit confirmed against a public source (HMRC, the SRA, the current CLC Accounts
              Code) or against our own live pricing page — PAYE for sole directors, CLC reconciliation timing, EMI
              valuations, indicative pricing plus VAT, filing scope, VAT and MTD qualifications, duplicate answers and
              corrupted characters. Answers are matched on their web address, so this button applies exactly the same
              wording on the live site. Running it twice changes nothing.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={() => run(true)} disabled={!!busy} variant="outline" className="rounded-full" data-testid="audit-preview">
              {busy === "dry" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Eye size={14} className="mr-1.5" />} Preview
            </Button>
            <Button onClick={() => run(false)} disabled={!!busy} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="audit-apply">
              {busy === "apply" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <PlayCircle size={14} className="mr-1.5" />} Apply corrections
            </Button>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm" data-testid="audit-stats">
          {[
            { label: "Corrections in this audit", value: status.corrections_available },
            { label: "Already applied", value: status.already_applied },
            { label: "Still to apply", value: pending },
            { label: "Duplicates made aliases", value: status.aliases_set },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-50 p-4">
              <p className="text-xl font-display font-bold text-navy">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {status.answers_with_corruption > 0 && (
          <p className="mt-4 text-xs text-orange-dark flex items-center gap-1.5">
            <AlertTriangle size={14} /> {status.answers_with_corruption} answers still contain a corrupted character — applying the corrections fixes them.
          </p>
        )}
        {status.missing_slugs.length > 0 && (
          <p className="mt-3 text-xs text-slate-400">
            {status.missing_slugs.length} corrected answers aren't in this copy of the library (they exist on the live
            site only): {status.missing_slugs.slice(0, 4).join(", ")}{status.missing_slugs.length > 4 ? "…" : ""}
          </p>
        )}
      </div>

      {report && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="audit-report">
          <h3 className="font-display font-bold text-navy">
            {report.dry_run ? "Preview — nothing was changed" : "Corrections applied"}
          </h3>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.updated.length}</strong> answers rewritten</p>
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.corruption_fixed.length}</strong> corrupted characters fixed</p>
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.not_found.length}</strong> not in this library</p>
          </div>
          <div className="mt-4 max-h-72 overflow-y-auto">
            {report.updated.map((u) => (
              <p key={u.slug} className="text-xs text-slate-600 py-1.5 border-b border-slate-100 last:border-0">
                <span className="font-mono text-[11px] text-orange mr-2">{u.finding}</span>{u.slug}
                <span className="text-slate-400"> — {u.fields.join(", ")}</span>
              </p>
            ))}
            {report.sections_not_found.length > 0 && (
              <p className="text-xs text-orange-dark mt-3">Sections not found (check the wording by hand): {report.sections_not_found.join("; ")}</p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-navy flex items-center gap-2">
          <ShieldCheck size={16} className="text-orange" /> Waiting on CASS — not changed
        </h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
          These findings depend on CASS policy, engagement terms or authorisations. Nothing has been altered: once Paul
          confirms each point we'll apply the wording.
        </p>
        <div className="mt-4 space-y-3" data-testid="audit-needs-confirmation">
          {(status.needs_confirmation || []).map((n) => (
            <div key={n.ref} className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-bold text-navy">
                <span className="font-mono text-[11px] text-orange mr-2">{n.ref}</span>{n.topic}
              </p>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{n.question}</p>
              <p className="text-[11px] text-slate-400 mt-2">Affects: {n.affects}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
